import LOCAL_STORAGE from 'utils/localStorage/Constants';
import Storage from 'utils/localStorage/Storage';
import AuthActions from 'actions/AuthActions';
import { HEADER_VALUE } from 'constants/authentication';
import RCPSCookies from 'utils/RCPSCookies';
import Cookies from 'utils/Cookies';
import Url from 'utils/Url';
import Location from 'utils/Location';
import LanguageLocale from 'utils/LanguageLocale';
import { validateReturnUrl } from 'utils/returnUrlValidator';
import { readMarketingParamsFromUrl, MAX_PARAM_LEN } from 'utils/signInMarketingParams';
// SIM-24 (ACS-4418) Option 2: pre-redirect passkey at the trigger choke-point.
//
// F-007 fix: `utils/passkeys` is NOT imported here — it transitively pulls in `utils/User`
// → `store/Store` → `RwdBasketActions` → `services/api/basket/getSamples` → `services/api/ufeApi`
// → `services/auth/newFlow/api/newAuthUfeApi`, and that last module destructures
// `isAuthServiceUrl` from `authenticationUtils.default` at MODULE LOAD TIME (line 44). While
// Authentication.js is still evaluating its own imports, that destructure sees an undefined
// default and throws `Cannot access '__WEBPACK_DEFAULT_EXPORT__' before initialization` — a
// hard cycle that breaks both Karma (webpack bundle) and Jest, and can crash real production
// builds when chunk-load order isn't guaranteed. Since the only thing we needed from
// utils/passkeys is the trivial `isPasskeyEnabled()` config getter (see below), it is inlined
// as a local `const` — removing the import entirely and cutting the cycle at its source.
import { loadBeyondIdentityUtils } from 'utils/lazyPasskey';
import { classifyPasskeyError, logPasskeyForDynatrace } from 'utils/passkeyDynatrace';
import { passkeySignInLock } from 'constants/passkeys';
import { showPasskeyFailureModalIfNeeded } from 'utils/passkeyFailureModal';

// F-007 fix — one-line inline of utils/passkeys.js:11 `isPasskeyEnabled`. Same
// `Sephora.configurationSettings?.passkey?.isPasskeyEnabled` read the exported function
// performs. Kept in sync by convention (both live in the SIM-24 changeset); if the config
// path ever changes, update both call sites simultaneously.
const isPasskeyEnabled = () => Sephora.configurationSettings?.passkey?.isPasskeyEnabled;

const { isRCPSCookieBasedAuthEnabled } = RCPSCookies;

const authenticationUtils = {};

/*
 * Show the signin modal and apply postponed promo after Login if an apple pay session
 *
 * Note: this is needed only on basket and checkout but needs to be on all ways of logging in on
 * those pages, thus its presence here as a universal decorator
 */
authenticationUtils.showSignInModalWithPromoDecoration = async function (
    resolve,
    reject,
    isNewUserFlow = false,
    analyticsData = {},
    source = '',
    showBeautyPreferencesFlow,
    withExtraParams = false,
    headerValue,
    contextOptions = {}
) {
    // Required on the method level bc/ of a circular dependency otherwise.
    const [ActionsModule, storeModule] = await Promise.all([import('actions/Actions'), import('store/Store')]);
    const Actions = ActionsModule.default;
    const storeLocal = storeModule.default;

    const decoratedResolve = function (data) {
        const applePaySession = storeLocal.getState().applePaySession;

        // TODO: Remove isActive ApplePay check when you need to enable it for any signIn
        if (applePaySession.isActive) {
            const promo = storeLocal.getState().promo;

            if (promo.afterLogin && promo.afterLogin.length) {
                import('utils/Promos').then(promoUtils => {
                    promoUtils.default.applyPromo(...promo.afterLogin).then(() => {
                        resolve(data);
                    });
                });
            } else {
                resolve(data);
            }
        } else {
            resolve(data);
        }
    };

    const modalOptions = {
        isOpen: true,
        callback: decoratedResolve,
        isNewUserFlow,
        analyticsData,
        errback: reject,
        source,
        showBeautyPreferencesFlow
    };

    if (withExtraParams) {
        modalOptions.extraParams = { headerValue: '/gway/v1/dotcom/auth/v2/refreshToken' };
    }

    if (headerValue) {
        modalOptions.extraParams = { headerValue: HEADER_VALUE.USER_CLICK };
    }

    const { guestEventServicesEnabled } = contextOptions;

    if (guestEventServicesEnabled) {
        const {
            potentialServiceBIPoints, onContinueAsAGuest, keepMeSignedIn, storeId, bookingType
        } = contextOptions;

        const updatedExtraParams = Object.assign({}, modalOptions.extraParams, {
            onContinueAsAGuest,
            keepMeSignedIn,
            storeId,
            bookingType
        });
        const updatedModalOptions = Object.assign({}, modalOptions, {
            isGuestBookingEnabled: true,
            potentialServiceBIPoints,
            extraParams: updatedExtraParams
        });

        storeLocal.dispatch(Actions.showSignInWithMessagingModal(updatedModalOptions));
    } else {
        storeLocal.dispatch(Actions.showSignInModal(modalOptions));
    }
};

/**
 * Renders the sign-in / register overlay if needed.  then redirects/calls the desired function
 *
 * Note: only checks for signed in/recognized versus anonymous, does not differentiate between
 * signed in and recognized (API call return status will handle that accordingly)
 *
 * @param {String} redirect        *TODO* Optional: path to redirect,
 *                                  else redirected to same page or home
 * @param {object} objdata         *TODO* Optional: object for storing the temporary data required
 *                                  by functionality which needs
 *                                 to be performed after registration is done
 *                                 objdata format should be : {
 *                                      sActionType : 'Loves',
 *                                      oActionData : {}
 *                                 }
 **/
authenticationUtils.requireAuthentication = function (
    requiredForRecognized = false,
    isNewUserFlow,
    analyticsData,
    source,
    showBeautyPreferencesFlow = false,
    headerValue,
    contextOptions = {}
) {
    return new Promise(function (resolve, reject) {
        import('utils/User').then(userUtils => {
            userUtils.default.validateUserStatus().then(({ auth }) => {
                const profileStatus = auth.profileStatus;
                const PROFILE_STATUS = userUtils.default.PROFILE_STATUS;

                if (
                    profileStatus === PROFILE_STATUS.ANONYMOUS ||
                    (PROFILE_STATUS.RECOGNIZED_STATUSES.includes(profileStatus) && requiredForRecognized)
                ) {
                    // SIM-11 Layer B gate: route to /signin when the new flow is on, EXCEPT for the
                    // guest-booking messaging path (guestEventServicesEnabled), which stays on the legacy
                    // showSignInWithMessagingModal (descoped — ACS-4141). Return-and-retry: promise abandoned.
                    if (authenticationUtils.isNewSignInFlowEnabled() && !contextOptions.guestEventServicesEnabled) {
                        authenticationUtils.navigateToSignIn();
                    } else {
                        authenticationUtils.showSignInModalWithPromoDecoration(
                            resolve,
                            reject,
                            isNewUserFlow,
                            analyticsData,
                            source,
                            showBeautyPreferencesFlow,
                            null,
                            headerValue,
                            contextOptions
                        );
                    }
                } else {
                    resolve();
                }
            });
        });
    });
};

// TODO THOMAS 17.4:
//  refactor various files that are using the above requireAuthentication function
//  so that those files use new decorator that calls this promise function
//  files include: ProductQuickLookMessage.c.js, AccountGreeting.c.js, InlineBasket.c.js,
//  ProductLove.c.js, RewardItem.c.js, PleaseSignIn.c.js
authenticationUtils.requireRecognizedAuthentication = function () {
    return new Promise((resolve, reject) => {
        import('utils/User').then(userUtils => {
            userUtils.default.validateUserStatus().then(({ auth }) => {
                var profileStatus = auth.profileStatus;

                if (profileStatus === 0) {
                    // SIM-11 Layer B gate: route to /signin when the new flow is on. Return-and-retry.
                    if (authenticationUtils.isNewSignInFlowEnabled()) {
                        authenticationUtils.navigateToSignIn();
                    } else {
                        authenticationUtils.showSignInModalWithPromoDecoration(resolve, reject);
                    }
                } else {
                    resolve();
                }
            });
        });
    });
};

/**
 * Checks if the current session is expired by reading the sis (or legacy profileStatus) cookie.
 * Cookie format: "{profileSecurityStatus}|{accessTokenExpiryUnixSeconds}"
 * Returns true when: cookie missing, status < 2 (anonymous), expiry timestamp in the past, or sks (refresh token) cookie missing.
 * @returns {boolean}
 */
authenticationUtils.isSessionExpired = function () {
    const sisCookie = Cookies.read('sis') || Cookies.read('profileStatus');

    if (!sisCookie) {
        return true;
    }

    const parts = sisCookie.split('|');
    const status = parseInt(parts[0], 10);

    if (isNaN(status) || status < 2) {
        return true;
    }

    const expiry = parts[1] ? parseInt(parts[1], 10) : 0;

    if (expiry > 0 && Date.now() / 1000 > expiry) {
        return true;
    }

    const sksCookie = Cookies.read('sks');

    if (!sksCookie) {
        return true;
    }

    return false;
};

authenticationUtils.requireLoggedInAuthentication = async function () {
    const [ssiApiModule, fingerprintModule] = await Promise.all([
        import(/* webpackMode: "eager" */ 'services/api/ssi'),
        import(/* webpackMode: "eager" */ 'services/Fingerprint')
    ]);
    const ssiApi = ssiApiModule.default;
    const { setupFingerprint } = fingerprintModule.default;

    return new Promise((resolve, reject) => {
        setupFingerprint(deviceFingerprint => {
            import('utils/User').then(userUtils => {
                userUtils.default.validateUserStatus().then(({ auth }) => {
                    var profileStatus = auth.profileStatus;
                    const PROFILE_STATUS = userUtils.default.PROFILE_STATUS;

                    if (profileStatus === PROFILE_STATUS.ANONYMOUS) {
                        // SIM-11 Layer B gate: route to /signin when the new flow is on. Return-and-retry.
                        if (authenticationUtils.isNewSignInFlowEnabled()) {
                            authenticationUtils.navigateToSignIn();
                        } else {
                            authenticationUtils.showSignInModalWithPromoDecoration(resolve, reject);
                        }
                    } else if (PROFILE_STATUS.RECOGNIZED_STATUSES.includes(profileStatus)) {
                        if (Sephora.configurationSettings.isOptInSSIMWebEnabled) {
                            // Check if tokens are valid before calling autoLogin to prevent unnecessary refresh calls
                            // Issue reported:  https://jira.sephora.com/browse/ACS-2522
                            const RefreshToken = window.Sephora.Util?.RefreshToken;
                            const tokensAreValid = RefreshToken && RefreshToken.isAccessTokenValid && RefreshToken.isAccessTokenValid();

                            if (tokensAreValid) {
                                // Tokens are still valid, skip autoLogin call
                                resolve();
                            } else {
                                // Tokens are invalid or unavailable, proceed with autoLogin
                                ssiApi
                                    .autoLogin({ deviceFingerprint })
                                    .then(tokensResponse => {
                                        authenticationUtils.updateProfileStatus({
                                            profileSecurityStatus: [tokensResponse.profileSecurityStatus],
                                            accessToken: [tokensResponse.accessToken, tokensResponse.atExp],
                                            refreshToken: [tokensResponse.refreshToken, tokensResponse.rtExp]
                                        });
                                        resolve();
                                    })
                                    .catch(() => {
                                        // SIM-11 Layer B gate: route to /signin when the new flow is on.
                                        if (authenticationUtils.isNewSignInFlowEnabled()) {
                                            authenticationUtils.navigateToSignIn();
                                        } else {
                                            authenticationUtils.showSignInModalWithPromoDecoration(resolve, reject, false, {}, '', false, true);
                                        }
                                    });
                            }
                        } else {
                            // SIM-11 Layer B gate (F-10): route to /signin when the new flow is on. Return-and-retry.
                            if (authenticationUtils.isNewSignInFlowEnabled()) {
                                authenticationUtils.navigateToSignIn();
                            } else {
                                authenticationUtils.showSignInModalWithPromoDecoration(resolve, reject);
                            }
                        }
                    } else {
                        resolve();
                    }
                });
            });
        });
    });
};

authenticationUtils.isAuthServiceEnabled = () => {
    const isKillSwitchEnabled = Sephora.configurationSettings?.isAuthServiceEnabled || false;

    return isKillSwitchEnabled;
};

authenticationUtils.isAuthServiceUrl = url => {
    const authServiceUrlRegExp = /(\/api\/auth\/v1\/|\/gway\/v1\/dotcom\/auth\/v1\/|\/gway\/v1\/dotcom\/auth\/passkeylogin)/;

    return authServiceUrlRegExp.test(url);
};

authenticationUtils.storeAuthTokens = ({
    accessToken, refreshToken, email, accessTokenExpiry, refreshTokenExpiry
}) => {
    if (accessToken && refreshToken) {
        Storage.local.setItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN, accessToken, accessTokenExpiry);
        Storage.local.setItem(LOCAL_STORAGE.AUTH_REFRESH_TOKEN, refreshToken, refreshTokenExpiry);

        if (email) {
            Storage.local.setItem(LOCAL_STORAGE.USER_EMAIL, email);
        }
    }
};

authenticationUtils.getAccessToken = () => {
    return Storage.local.getItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN);
};

authenticationUtils.getFingerPrint = () => {
    return window.FingerprintJS.load().then(function (fp) {
        return fp.get().then(function (result) {
            return result.visitorId;
        });
    });
};

const authenticationTokensMapping = {
    [LOCAL_STORAGE.AUTH_ACCESS_TOKEN]: {
        localStorageName: LOCAL_STORAGE.AUTH_ACCESS_TOKEN
    },
    [LOCAL_STORAGE.AUTH_REFRESH_TOKEN]: {
        localStorageName: LOCAL_STORAGE.AUTH_REFRESH_TOKEN
    },
    [LOCAL_STORAGE.PROFILE_SECURITY_STATUS]: {
        localStorageName: LOCAL_STORAGE.PROFILE_SECURITY_STATUS
    },
    [LOCAL_STORAGE.USER_EMAIL]: {
        localStorageName: LOCAL_STORAGE.USER_EMAIL
    },
    [LOCAL_STORAGE.HAS_IDENTITY]: {
        localStorageName: LOCAL_STORAGE.HAS_IDENTITY
    }
};

authenticationUtils.updateProfileStatus = authData => {
    // Required on the method level bc/ of a circular dependency otherwise.
    import('store/Store').then(({ default: storeLocal }) => {
        const { profileSecurityStatus = null, hasIdentity = null } = authData;

        if (profileSecurityStatus != null) {
            storeLocal.dispatch(AuthActions.updateProfileStatus(profileSecurityStatus[0], false));

            // Set profileSecurityStatus cookie if cookie-based auth is enabled
            if (isRCPSCookieBasedAuthEnabled()) {
                const profileStatusTTL = authData?.accessToken?.[1];
                const profileStatusCookieValue = `${profileSecurityStatus[0]}|${profileStatusTTL}`;

                // Set the profileStatus cookie with the appropriate TTL
                // profileStatusTTL: expiration timestamp in seconds (Unix time)
                const nowMs = Date.now();
                const expirationMs = profileStatusTTL * 1000;

                const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

                // Remaining time until expiration (in ms)
                const remainingMs = expirationMs - nowMs;

                // Convert remaining time to days for cookie maxAge
                const maxAgeInDays = remainingMs / MILLISECONDS_PER_DAY;

                Cookies.write('profileStatus', profileStatusCookieValue, maxAgeInDays, true, false);
            }
        }

        if (hasIdentity != null) {
            storeLocal.dispatch(AuthActions.updateHasIdentity(hasIdentity[0]));
        }
    });

    Object.entries(authData).forEach(([key, value]) => {
        if (authenticationTokensMapping[key]) {
            const [propValue, expiry = null] = value;

            Storage.local.setItem(authenticationTokensMapping[key].localStorageName, propValue, expiry);
        }
    });

    // In new auth flow, still persist hasIdentity to localStorage so that
    // shouldShowPasskeyOptin() fallback works (e.g. OrderConfirmation, BeautyInsider)
    if (authData.isNewAuthFlow) {
        if (authData.hasIdentity != null) {
            Storage.local.setItem(LOCAL_STORAGE.HAS_IDENTITY, authData.hasIdentity[0]);
        }

        return;
    }

    authenticationUtils.updateAuthenticationFlag();

    // Set the token refresh timer after tokens are stored
    // This ensures proactive refresh works after login
    // Use optional call ?.() to guard against RefreshTokenFacade (new headscript) not having _setTokenTimer
    if (authData.accessToken && authData.refreshToken) {
        window.Sephora.Util?.RefreshToken?._setTokenTimer?.();
    }
};

authenticationUtils.updateAuthenticationFlag = function () {
    // Return early if we were in the new auth flow since it doest use timers or old main head script code
    if (isRCPSCookieBasedAuthEnabled()) {
        return;
    }

    // RefreshToken is now available globally via Sephora.Util.RefreshToken from main.headScript.js
    window.Sephora.Util.isLoggedIn = window.Sephora.Util?.RefreshToken?.isUserLoggedIn();
    // Set Token timer for proactive refresh
    // Use optional call ?.() to guard against RefreshTokenFacade (new headscript) not having _setTokenTimer
    window.Sephora.Util?.RefreshToken?._setTokenTimer?.();
};

authenticationUtils.SIGN_IN_SOURCES = {
    ACCOUNT_GREETING: 'account-greeting',
    PAGE_LOAD: 'page-load',
    RECAP_SIGN_IN_CTA: 'recap-sign-in-cta',
    PROMOTION_LIST_SIGN_IN_CTA: 'promotion-list-sign-in-cta'
};

authenticationUtils.getHasIdentity = auth => auth.hasIdentity || Storage.local.getItem(LOCAL_STORAGE.HAS_IDENTITY);

/**
 * Client-side gate for the new /signin flow (SIM-10 / ACS-4134). Mirrors the
 * SIM-1 server-side two-layer gate so the FE agrees with what the route served:
 *   - BR-046 killswitch: Config Hub `signin_modernization_enabled` (master off-switch)
 *   - BR-047 ramp cohort: Akamai edge cookie `rcps_signInModernization === 'true'`
 * Both must be true. Named for the capability it gates (NOT "modernization"), so
 * the name stays correct once the legacy flow is retired.
 * @returns {boolean}
 */
authenticationUtils.isNewSignInFlowEnabled = function () {
    const killswitch = typeof Sephora !== 'undefined' && Sephora.configurationSettings?.['signin_modernization_enabled'];
    const isInCohort = Cookies.read('rcps_signInModernization') === 'true';

    return Boolean(killswitch) && isInCohort;
};

/**
 * Builds the /signin URL with the validated returnUrl and preserved marketing params. Extracted
 * so the SIM-24 async passkey path can reuse the same target for both success-stays and
 * fail-redirects code paths (no duplication).
 *
 * ACS-4437 (ADR-TS-013): accepts an optional `extraParams` object. Keys are appended to the
 * target query string after validation (reserved-key skip + same-key skip + MAX_PARAM_LEN cap)
 * so callers can carry a credential (RegisterEmailLookup "already registered" flow) without
 * risking an open-redirect via the returnUrl slot or overrunning any marketing param.
 *
 * @param {string} [returnUrl] - Optional explicit returnUrl; defaults to the current page.
 * @param {object} [extraParams] - ACS-4437 (ADR-TS-013): optional extra query params
 *   appended to the /signin target (e.g. { credential } for the registration-redirect
 *   credential carry). Backward-compatible: omitted for existing single-arg callers.
 *   SIM-17 (ACS-4141) DEFER-2b reuses this for the account-closure/guest-checkout
 *   credential carry: navigateToSignIn(returnUrl, { credential }). Each extra param is
 *   validated + capped (MAX_PARAM_LEN) and cannot overwrite the SIM-3-validated returnUrl.
 */
function buildSignInRedirectTarget(returnUrl, extraParams) {
    const location = Location.getLocation(true) || {};
    // SIM-24 (ACS-4418) F-006 fix: include location.hash so origin URL fragments (e.g.
    // /product/P12345#reviews, /account#orders) survive the sign-in round-trip. Prior to
    // this fix the fragment was silently dropped at capture — useReturnUrl.js:101 already
    // re-emits target.hash on the return leg but always received an empty string because
    // buildSignInRedirectTarget captured pathname+search only. validateReturnUrl accepts
    // hash-bearing same-origin URLs verbatim (returnUrlValidator.js), so no downstream
    // change is required. Remediates F-006 across every trigger-site flow (8 of 11
    // audited scenarios).
    const currentPath = (location.pathname || '') + (location.search || '') + (location.hash || '');
    const current = returnUrl || currentPath;

    let locale;

    try {
        locale = LanguageLocale.getCurrentLanguageCountryCode();
    } catch (e) {
        // SSR/early-boot: locale globals may be uninitialized. Let the validator derive the safe fallback.
        locale = undefined;
    }

    const validated = validateReturnUrl(current, locale);
    const marketing = readMarketingParamsFromUrl(location.search || '');

    const target = new URL('/signin', window.location.origin);
    target.searchParams.set('returnUrl', validated);

    Object.entries(marketing).forEach(([key, value]) => {
        if (value && value.length <= MAX_PARAM_LEN) {
            target.searchParams.set(key, value);
        }
    });

    // ACS-4437 (ADR-TS-013): append caller-provided extraParams AFTER returnUrl and marketing
    // params. Each entry is validated: skip when value is null/undefined, skip the reserved
    // `returnUrl` key (SIM-3 open-redirect control — never let a caller clobber the validated
    // return target), skip when the key is already set (marketing params must not be
    // overwritten), and drop values that exceed MAX_PARAM_LEN. Rejected entries are silently
    // dropped rather than being clobbered by an unsafe value.
    if (extraParams && typeof extraParams === 'object') {
        Object.entries(extraParams).forEach(([key, value]) => {
            if (value == null) {
                return;
            }

            const stringValue = String(value);

            if (key === 'returnUrl' || target.searchParams.has(key) || stringValue.length > MAX_PARAM_LEN) {
                return;
            }

            target.searchParams.set(key, stringValue);
        });
    }

    return target;
}

/**
 * SIM-24 (ACS-4418) trigger-point in-place WebAuthn for a single-passkey device. Mirrors the
 * PasskeySelectorModal dispatch shape so the reducer path is identical.
 *
 * `suppressErrorModal: true` silences login.js's LEGACY handlePasskeyError modal-on-close
 * (which would reopen the legacy SignInModal — unwanted in the new flow). Client-side error
 * surface (modal + Adobe Analytics prop28/prop48) is delegated to utils/passkeyFailureModal.js
 * which fires for EVERY passkey error (matching legacy handlePasskeyError coverage exactly),
 * with the caller supplying its own onDismiss for post-modal navigation. This preserves the
 * legacy tracking + user-visible feedback set unchanged; no error class silently drops.
 *
 * Failure discrimination: this function itself does NOT distinguish preflight failure vs
 * WebAuthn/dispatch failure — that's captured via `error.operation` / `error.category` /
 * `error.reason` attached by login.js to every synthesized passkey rejection (Fix 1).
 * The caller's catch fires showPasskeyFailureModalIfNeeded which reads those fields.
 *
 * Outcome observation via callbacks, NOT `await dispatch`: UserActions.signIn's thunk is
 * fire-and-forget (`fingerPrint.setupFingerprint(executeLogin)` returns undefined) and its
 * internal .catch swallows the passkey rejection, so awaiting the dispatch would resolve to
 * undefined before WebAuthn even prompts. We use the positional successCallback (arg 5) and
 * failureCallback (arg 6) that UserActions.signIn invokes from its own .then/.catch, and
 * Promise-wrap them so the caller sees the actual outcome.
 *
 * passkeySignInLock is acquired around the sign-in so any 401 that fires on a background
 * request during the passkey flow (handled by ufeApi / newAuthUfeApi) defers its manual
 * sign-in prompt for the lock's hold window, instead of racing us with a competing modal.
 * Released in a finally so both success and failure paths drop it.
 */
async function attemptInPlacePasskeySignIn(passkey) {
    const [{ default: store }, { default: UserActions }] = await Promise.all([import('store/Store'), import('actions/UserActions')]);

    passkeySignInLock.acquire();

    try {
        await new Promise((resolve, reject) => {
            store.dispatch(
                UserActions.signIn(
                    passkey?.identity?.emailAddress,
                    undefined,
                    true,
                    false,
                    () => resolve(),
                    reason => reject(reason || new Error('Passkey sign-in failed')),
                    false,
                    null,
                    false,
                    false,
                    {},
                    {
                        isPasskeyLogin: true,
                        passkeyId: passkey.passkeyId,
                        passkeyCredentialId: passkey.id,
                        identityId: passkey?.identity?.id,
                        suppressErrorModal: true
                    }
                )
            );
        });
        // AC-8 success + AC-9: no redirect, no returnUrl consumed — the user's session is now
        // authenticated and they stay on the page they were on. AC-10: the original action is NOT
        // replayed (return-and-retry, consistent with TADR-SIM11-001).
    } finally {
        passkeySignInLock.release();
    }
}

/**
 * SIM-24 (ACS-4418) trigger-point selector for multi-passkey devices. Dispatches the shared
 * PasskeySelectorModal via Redux. successCallback = stay in place (modal already dispatched
 * UserActions.signIn); errback (dismiss / "sign in with another account" / close) = redirect
 * to /signin with the passkeyOffered=1 suppression flag so /signin skips its own prompts.
 *
 * SIM-24 (ACS-4418) CR-4 fix — RETURN VALUE contract: the returned Promise settles only when
 * the user actually makes a choice (successful selection resolves; dismiss / close / "another
 * account" also resolves, AFTER `redirectWithSuppression` has kicked off the redirect). This
 * is load-bearing for the F-012 in-flight guard in `authenticationUtils.navigateToSignIn` —
 * the guard is held while `_navigateToSignInImpl` is awaiting this call, so any concurrent
 * `navigateToSignIn` triggered while the selector is visible (double-click, second
 * requireAuthentication check, stale `Actions.showSignInModal` re-route) shares the pending
 * promise and does NOT re-preflight the SDK, re-enumerate passkeys, or re-dispatch the
 * selector with fresh callbacks (which would silently clobber the first invocation's
 * `redirectWithSuppression` closure and could send the user to the wrong returnUrl).
 *
 * SINGLE-passkey path (attemptInPlacePasskeySignIn) already holds the guard for the full
 * WebAuthn ceremony because it awaits the signIn Promise; this brings MULTIPLE to parity.
 *
 * We never REJECT this Promise — both callback branches call `resolve()`. The outer
 * try/catch in `_navigateToSignInImpl` remains as a defense-in-depth against a synchronous
 * store.dispatch throw (which the Promise executor would catch and turn into a rejection),
 * NOT for user-dismiss handling. Any additional teardown belongs inside the callbacks.
 */
async function openTriggerPasskeySelector(passkeys, redirectWithSuppression) {
    const [{ default: store }, { default: Actions }] = await Promise.all([import('store/Store'), import('Actions')]);

    await new Promise(resolve => {
        store.dispatch(
            Actions.showPasskeySelectorModal({
                isOpen: true,
                passkeys,
                successCallback: () => {
                    // Stay: PasskeySelectorModal (post-Fix-2) already awaited the real
                    // signIn callback before invoking us, so the user is authenticated on
                    // their origin page. Resolve to release the outer F-012 guard.
                    resolve();
                },
                errback: () => {
                    // User dismissed / picked "sign in with another account" / login failed.
                    // Fire the redirect FIRST (unchanged semantics — page starts unloading),
                    // then resolve so the outer async body of _navigateToSignInImpl completes
                    // and the F-012 guard clears just before the browser tears down this
                    // page context. Ordering matters here for the guard-then-redirect window:
                    // any concurrent trigger that arrived during the modal must NOT start a
                    // fresh WebAuthn on the outgoing page.
                    redirectWithSuppression();
                    resolve();
                },
                source: 'signInTrigger'
            })
        );
    });
}

/**
 * SIM-11 (ACS-4135) redirect choke-point + SIM-24 (ACS-4418, Option 2) pre-redirect passkey seam.
 * IMPLEMENTATION — the public entry is the `authenticationUtils.navigateToSignIn` wrapper below,
 * which adds SSR safety and the F-012 in-flight guard (see block comment above `inFlightNavigate`).
 *
 * Routes a sign-in trigger to the page-based /signin flow instead of opening the legacy modal,
 * preserving the origin page (returnUrl) and marketing params (campaign/referral) across the
 * redirect. Return-and-retry: NO continuation (callback/errback) is captured or forwarded — the
 * user re-triggers the action after landing back authenticated.
 *
 * SIM-24 (Option 2, ADR-TS-011 flipped 2026-08-10): before redirecting, attempt passkey sign-in
 * IN PLACE so users with a passkey never leave their current page.
 *   - 0 passkeys / SDK error / gate off  -> redirect immediately (unchanged ACS-4135 behavior)
 *   - 1 passkey                          -> in-place WebAuthn; success = stay, fail = redirect
 *                                            with `passkeyOffered=1` suppression flag (AC-11)
 *   - 2+ passkeys                        -> open PasskeySelectorModal; success = stay, dismiss
 *                                            / "another account" / close = redirect with
 *                                            `passkeyOffered=1`
 * The suppression flag tells /signin (SignIn.f.jsx) to skip its own passkey prompts (SINGLE
 * auto-signin and MULTIPLE selector), and the /signin page strips it from the URL after
 * reading so a refresh / bookmark / share does not permanently suppress passkey detection.
 *
 * Every existing caller in this file uses fire-and-forget (no awaits) and treats it as a
 * navigation. The SDK load adds ~100-500ms latency at trigger sites; failures degrade to the
 * plain redirect (no user-visible error, no replay of the original action per AC-10 /
 * TADR-SIM11-001).
 *
 * @param {string} [returnUrl] - Optional explicit returnUrl; defaults to the current page.
 * @returns {Promise<void>}
 */
// SIM-24 (ACS-4418) F-012 in-flight guard. Prevents concurrent navigateToSignIn callers from
// racing to dispatch WebAuthn twice.
//
// Symptom without the guard: user clicks a sign-in trigger → WebAuthn biometric prompt shows.
// ~100ms later a SECOND navigateToSignIn fires (an autoLogin failure catch at line 278/287,
// a stale Actions.showSignInModal({isOpen:true}) dispatch re-routed through Actions.js:310, a
// second click, or requireAuthentication firing from a different UI component). That second
// call runs its own preflight, finds the same 1 passkey, and dispatches UserActions.signIn →
// BI SDK authenticate() — the BROWSER REJECTS the concurrent WebAuthn request (NotAllowedError
// / AbortError). The second call's catch treats that as a normal failure (operation='Authenticate'),
// showPasskeyFailureModalIfNeeded's silent path runs redirectWithSuppression, and the page
// navigates to /signin out from under the FIRST call's still-open biometric prompt. User sees
// the OS biometric dialog on top of a navigating page — the exact reported symptom.
//
// Guard: subsequent callers share the first invocation's promise until it settles. Second
// caller returns the pending promise without starting a new preflight or dispatch. When the
// first invocation resolves (success = stay, failure = redirect) the flag clears and future
// invocations (after user's session state has changed) run fresh.
//
// SINGLE case (WebAuthn dispatch): guard is essential — WebAuthn dispatches are NOT idempotent.
// MULTIPLE case (selector modal): guard is bonus safety — modal dispatches are effectively
// idempotent (same passkeys array, equivalent callbacks) but the guard avoids wasted SDK work.
// SSR: guard bypassed by the wrapper's `typeof window` check before any state is touched.
let inFlightNavigate = null;

const _navigateToSignInImpl = async function (returnUrl, extraParams) {
    // Defensive: callers already gate, but fall back to the legacy decorator if the gate is off.
    if (!authenticationUtils.isNewSignInFlowEnabled()) {
        authenticationUtils.showSignInModalWithPromoDecoration(
            () => {},
            () => {}
        );

        return;
    }

    const target = buildSignInRedirectTarget(returnUrl, extraParams);
    const redirectPlain = () => Url.redirectTo(target.pathname + target.search);
    const redirectWithSuppression = () => {
        target.searchParams.set('passkeyOffered', '1');
        Url.redirectTo(target.pathname + target.search);
    };

    // AC-12: passkey work is gated by BOTH isNewSignInFlowEnabled (already true above) AND
    // isPasskeyEnabled. When the platform gate is off, no SDK load, no WebAuthn, no flag.
    if (!isPasskeyEnabled()) {
        redirectPlain();

        return;
    }

    // ACS-4437 × SIM-24 composition contract: when the caller supplied a `credential` via
    // extraParams (RegisterEmailLookup "already registered" flow — ADR-TS-013), the identity
    // is already specified. Opportunistically firing WebAuthn for a device passkey that may
    // belong to a DIFFERENT identity would silently defeat ACS-4437's credential-carry (the
    // recognized-identity contract) and, on a shared device, sign the current visitor in as
    // the passkey owner instead of the requested one. Skip the preflight and redirect
    // immediately so /signin's credential-carry (auto-submit) can honor the specified
    // identity. Any other extraParams key (marketing carry, future flags) does NOT trigger
    // this bypass — only `credential` carries identity intent.
    if (extraParams && extraParams.credential) {
        redirectPlain();

        return;
    }

    let passkeys;

    // Preflight: load SDK + init + read device passkeys. A failure here means the user was
    // NEVER prompted (SDK didn't load, init crashed, or the platform authenticator refused
    // enumeration). Redirect WITHOUT the suppression flag so a subsequent /signin visit can
    // still evaluate passkeys normally (parity with the pre-SIM-24 behavior).
    try {
        const BeyondIdentityUtils = await loadBeyondIdentityUtils();
        await BeyondIdentityUtils.initialize();
        passkeys = await BeyondIdentityUtils.getPasskeys();
    } catch (error) {
        logPasskeyForDynatrace(classifyPasskeyError(error), 'TriggerAutoSignIn', error, 'signin-trigger');
        redirectPlain();

        return;
    }

    // AC-8 no-match: zero passkeys on device -> immediate redirect, NO suppression flag.
    if (!passkeys || passkeys.length === 0) {
        redirectPlain();

        return;
    }

    if (passkeys.length === 1) {
        // AC-8 single: in-place WebAuthn. Success = stay (no navigation, attemptInPlacePasskeySignIn
        // returns cleanly). Failure = the user WAS prompted and something went wrong (declined
        // biometric, browser cancelled, API rejected). Redirect with `passkeyOffered=1` so /signin
        // skips its own passkey prompts and we do not double-ask for the passkey they just walked
        // away from. This is separate from the preflight try/catch above — a decline reaches THIS
        // catch, not the SDK-load one, so the suppression flag survives.
        //
        // SIM-24 LEGACY-PARITY: fire modal + Adobe Analytics (via passkeyFailureModal ->
        // BeyondIdentityUtils.trackPasskeyError, prop28='passkey:{reason}', prop48='{errorId} | {msg}')
        // for EVERY passkey error — matches legacy handlePasskeyError coverage exactly. Redirect
        // is deferred to the modal's OK-callback so the user acknowledges the failure BEFORE the
        // page navigates. Applies uniformly to user cancel (reason='biometric'), pre-WebAuthn API
        // (reason='api:authenticate'), BI SDK internal (reason='sdk:authenticate' etc.), and
        // post-WebAuthn Sephora backend (reason='api:login'). No error class silently drops.
        try {
            await attemptInPlacePasskeySignIn(passkeys[0]);
        } catch (error) {
            // Prefer login.js-attached category/operation for accurate Dynatrace triage;
            // fall back to local classify for rejections that bypassed login.js.
            logPasskeyForDynatrace(
                error?.category || classifyPasskeyError(error),
                error?.operation || 'TriggerAutoSignIn',
                error,
                'signin-trigger-single'
            );
            // SIM-24 (ACS-4418) F-012 completion: AWAIT the failure modal so the outer
            // _navigateToSignInImpl does not return (and inFlightNavigate does not clear) until
            // the user dismisses the modal. Mirrors CR-4's await on openTriggerPasskeySelector
            // for the MULTIPLE-failure path — without this the F-012 in-flight guard clears
            // while the failure modal is still visible and concurrent navigateToSignIn calls
            // (background 401 defer expiring, second requireAuthentication fire, stale
            // Actions.showSignInModal re-route) re-preflight and re-fire WebAuthn BEHIND the
            // still-open failure modal (the exact race the F-012 guard was added to prevent).
            //
            // Defense-in-depth (audit F-009 / code-review #1): if showPasskeyFailureModalIfNeeded's
            // dynamic imports (store/Store, Actions, lazyPasskey) reject — stale-deploy chunk 404,
            // offline transition — the helper rejects before the modal is dispatched. The catch
            // below fires redirectWithSuppression manually so the user is not stranded on the
            // trigger page with no modal AND no redirect.
            try {
                await showPasskeyFailureModalIfNeeded(error, redirectWithSuppression);
            } catch (modalErr) {
                redirectWithSuppression();
            }
        }

        return;
    }

    // AC-8 multiple: show the shared selector at the trigger. successCallback stays put;
    // errback (dismiss / close / "sign in with another account") sends the user to /signin
    // with the suppression flag so we do not double-prompt on the page. openTriggerPasskeySelector
    // itself is a fire-and-forget dispatch; a failure to dispatch (which would be a bug) falls
    // through to a plain redirect since the user was never actually shown the selector.
    try {
        await openTriggerPasskeySelector(passkeys, redirectWithSuppression);
    } catch (error) {
        // Prefer login.js-attached category/operation when present (see F-001 fix).
        logPasskeyForDynatrace(
            error?.category || classifyPasskeyError(error),
            error?.operation || 'TriggerAutoSignIn',
            error,
            'signin-trigger-multi'
        );
        redirectPlain();
    }
};

/**
 * SIM-11 (ACS-4135) redirect choke-point + SIM-24 (ACS-4418, Option 2) pre-redirect passkey seam.
 *
 * Public entry. Thin wrapper over _navigateToSignInImpl that adds:
 *   1. SSR safety — no-op on the server.
 *   2. F-012 in-flight dedup — concurrent callers share the pending invocation instead of
 *      racing to dispatch WebAuthn twice (see the block comment above `inFlightNavigate`).
 *
 * Always returns a Promise so fire-and-forget callers, awaiting callers, and .then/.catch
 * chains all keep working with no signature change from the pre-guard implementation.
 *
 * @param {string} [returnUrl] - Optional explicit returnUrl; defaults to the current page.
 * @param {object} [extraParams] - ACS-4437 (ADR-TS-013) optional extra query params carried
 *   through to the /signin URL (e.g. `{ credential }` for the registration-redirect flow).
 *   Bypasses the F-012 in-flight cache: parameterized calls are user-gesture-driven and
 *   identity-scoped; coalescing them with a concurrent no-arg auto-fire caller would clobber
 *   the specified identity (and vice versa), so each parameterized call runs its own impl.
 * @returns {Promise<void>}
 */
authenticationUtils.navigateToSignIn = function (returnUrl, extraParams) {
    // SSR guard: never navigate during server render. Return a resolved Promise so callers
    // that `await` this function do not receive `undefined` (matches the pre-guard behavior
    // of an async function returning early — an implicit resolved Promise).
    if (typeof window === 'undefined') {
        return Promise.resolve();
    }

    // Bypass F-012 in-flight cache when extraParams is present. Parameterized calls
    // (credential-carry from RegisterEmailLookup, marketing/referral carries, future flags)
    // are user-gesture-driven and identity-scoped. Coalescing them with a concurrent no-arg
    // call would clobber the specified extraParams, and a subsequent no-arg caller finding
    // a cached extraParams-bearing promise would be routed to a URL it did not intend.
    // Since these calls arise from explicit user action (not from auto-fire scenarios that
    // need dedup), running them independently is safe and correct.
    if (extraParams) {
        return _navigateToSignInImpl(returnUrl, extraParams);
    }

    // F-012: share the in-flight invocation with concurrent callers. Prevents the "biometric
    // prompt open, page navigates behind it" race. Cleared in the finally so that AFTER the
    // first invocation settles (success = user stayed authenticated on origin, failure = user
    // redirected to /signin) subsequent legitimate triggers can start a fresh attempt.
    if (inFlightNavigate) {
        return inFlightNavigate;
    }

    inFlightNavigate = _navigateToSignInImpl(returnUrl).finally(() => {
        inFlightNavigate = null;
    });

    return inFlightNavigate;
};

export default authenticationUtils;
